import { Injectable, computed, inject, signal } from "@angular/core";
import { Observable, filter } from "rxjs";

import { ApiSessionService } from "../../api-session.service";
import {
  ApiUserCustomization,
  UserCustomization,
  apiUserCustomizationToUserCustomization as clean,
  UserCustomizationEditContactTypes,
  prepareUserCustomizationAddParams,
  prepareUserCustomizationDeleteParams,
  prepareUserCustomizationEditParams,
  prepareUserCustomizationEditFavouritesParams
} from "./user-customization";
import {
  ApiNoAuthUserSummary,
  apiNoAuthUserSummaryToUserCustomizationPartial as cleanNoAuthUserSummary
} from "../../apiResponse/no-auth-user-summary";
import { ApiEditAction, ApiBrowseAction, ApiReadAction } from "../../api-actions";
import { ParameterValue } from "../../util/api-action-description";
import { extractData } from "../../apiResponse/response-body-new";
import {
  genericApiAction,
  onsipApiBrowse,
  IApiError as ApiError
} from "../../onsip-api-action-new";
import { arrayToRecord } from "../../util/arrayToRecord";
import { OnsipAPIParameter } from "../../apiResponse/context";
import { ResourceState, initialResourceState } from "../../resource-state";
import { toObservable } from "@angular/core/rxjs-interop";
import { Contact } from "@onsip/common/interfaces/contact";
export { UserCustomization };

const debug = false;

@Injectable({ providedIn: "root" })
export class UserCustomizationService {
  protected readonly session = inject(ApiSessionService);

  private readonly editContactsConverterMap: Record<
    UserCustomizationEditContactTypes,
    (contacts: Array<Contact>) => Record<string, ParameterValue>
  > = {
    [UserCustomizationEditContactTypes.ADD]: prepareUserCustomizationAddParams,
    [UserCustomizationEditContactTypes.DELETE]: prepareUserCustomizationDeleteParams,
    [UserCustomizationEditContactTypes.EDIT]: prepareUserCustomizationEditParams,
    [UserCustomizationEditContactTypes.EDIT_FAVOURITES]:
      prepareUserCustomizationEditFavouritesParams
  };

  private readonly _state =
    signal<ResourceState<UserCustomization>>(initialResourceState<UserCustomization>());

  readonly state = this._state.asReadonly();
  readonly customizations = computed(() => Object.values(this._state().data));
  readonly orgCustomizations = computed(() =>
    this.customizations().filter(c => c.organizationId === this.session.organizationId())
  );
  readonly selfUserSignal = computed(() => this._state().data[this.session.userId()!]);
  readonly loading = computed(() => this._state().loading);
  readonly loaded = computed(() => this._state().loaded);
  readonly errors = computed(() => this._state().errors);

  private readonly selfUser$ = toObservable(this.selfUserSignal).pipe(
    filter((u): u is UserCustomization => !!u)
  );
  private readonly orgCustomizations$ = toObservable(this.orgCustomizations);

  get selfUser(): Observable<UserCustomization> {
    return this.selfUser$;
  }

  get orgState(): Observable<Array<UserCustomization>> {
    return this.orgCustomizations$;
  }

  constructor() {
    debug && toObservable(this.state).subscribe(s => console.warn("UserCustomizationService", s));
  }

  async userCustomizationBrowse(
    extraParams?: Record<string, ParameterValue>
  ): Promise<Array<UserCustomization>> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      const pages = await onsipApiBrowse({
        Action: ApiBrowseAction.UserCustomizationBrowse,
        SessionId: this.session.sessionId()!,
        OrganizationId: this.session.organizationId()!,
        Limit: 1000,
        OrderBy: "UserId",
        ...extraParams
      });
      const result: Record<string, UserCustomization> = {};
      for (const page of pages) {
        const items = extractData<Array<ApiUserCustomization>>(
          page,
          ApiBrowseAction.UserCustomizationBrowse,
          "UserCustomization",
          "UserCustomizations"
        ).map(clean);
        Object.assign(result, arrayToRecord(items, "userId"));
      }
      this._state.update(s => ({
        ...s,
        data: { ...s.data, ...result },
        loading: false,
        loaded: true,
        errors: []
      }));
      return Object.values(result);
    } catch (e) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }

  async userCustomizationRead(
    extraParams?: Record<string, ParameterValue>
  ): Promise<UserCustomization> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      const response = await genericApiAction({
        Action: ApiReadAction.UserCustomizationRead,
        SessionId: this.session.sessionId()!,
        UserId: this.session.userId()!,
        ...extraParams
      });
      const item = clean(
        extractData<ApiUserCustomization>(
          response,
          ApiReadAction.UserCustomizationRead,
          "UserCustomization"
        )
      );
      this._state.update(s => ({
        ...s,
        data: { ...s.data, [item.userId]: item },
        loading: false,
        loaded: true,
        errors: []
      }));
      return item;
    } catch (e: any) {
      if (e?.message === "No user customization for the given user ID.") {
        const paramUserId =
          (
            (
              e?.Response?.Context?.Request?.Parameters?.Parameter as Array<OnsipAPIParameter>
            )?.find((p: OnsipAPIParameter) => p.Name === "UserId") as OnsipAPIParameter
          )?.Value ??
          (extraParams?.UserId as string) ??
          this.session.userId()!;
        this._state.update(s => ({ ...s, loading: false, errors: [] }));
        return this.userCustomizationPersonalPageInfoEdit({}, paramUserId);
      }
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }

  async userCustomizationPersonalPageInfoEdit(
    {
      blurb,
      facebook,
      linkedin,
      title,
      twitter
    }: { blurb?: string; facebook?: string; linkedin?: string; title?: string; twitter?: string },
    givenUserId?: string
  ): Promise<UserCustomization> {
    const targetId = givenUserId ?? this.session.userId()!;
    const existing = this._state().data[targetId]?.userInfo?.personalPageInfo;

    const params: Record<string, ParameterValue> = {
      "PersonalPageInfo[Blurb]": blurb !== undefined ? blurb : (existing?.blurb ?? ""),
      "PersonalPageInfo[Facebook]": facebook !== undefined ? facebook : (existing?.facebook ?? ""),
      "PersonalPageInfo[Linkedin]": linkedin !== undefined ? linkedin : (existing?.linkedin ?? ""),
      "PersonalPageInfo[Title]": title !== undefined ? title : (existing?.title ?? ""),
      "PersonalPageInfo[Twitter]": twitter !== undefined ? twitter : (existing?.twitter ?? "")
    };
    if (givenUserId !== undefined) params.UserId = givenUserId;

    return this._editAndMerge(
      ApiEditAction.UserCustomizationPersonalPageInfoEdit,
      params,
      targetId
    );
  }

  async userCustomizationAvatarEdit(
    extraParams?: Record<string, ParameterValue>
  ): Promise<UserCustomization> {
    return this._editAndMerge(ApiEditAction.UserCustomizationAvatarEdit, extraParams ?? {});
  }

  editContact(
    type: UserCustomizationEditContactTypes,
    contacts: Array<Contact>
  ): Promise<UserCustomization> {
    return this.userCustomizationEdit(this.editContactsConverterMap[type](contacts));
  }

  async userCustomizationEdit(params: Record<string, ParameterValue>): Promise<UserCustomization> {
    return this._editAndMerge(ApiEditAction.UserCustomizationEdit, params);
  }

  ingestFromNoAuthUserSummaries(items: Array<ApiNoAuthUserSummary>): void {
    const partials = arrayToRecord(items.map(cleanNoAuthUserSummary), "userId");
    this._state.update(s => {
      const merged: Record<string, UserCustomization> = { ...s.data };
      for (const [id, partial] of Object.entries(partials)) {
        merged[id] = {
          ...(merged[id] ?? ({} as UserCustomization)),
          ...partial
        } as UserCustomization;
      }
      return { ...s, data: merged };
    });
  }

  ingestFromNoAuthUserSummary(item: ApiNoAuthUserSummary): void {
    this.ingestFromNoAuthUserSummaries([item]);
  }

  removeUser(userId: string): void {
    this._state.update(s => {
      const data = { ...s.data };
      delete data[userId];
      return { ...s, data };
    });
  }

  dispose(): void {
    this._state.set(initialResourceState<UserCustomization>());
  }

  private async _editAndMerge(
    action: ApiEditAction,
    params: Record<string, ParameterValue>,
    targetId?: string
  ): Promise<UserCustomization> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      const response = await genericApiAction({
        Action: action,
        SessionId: this.session.sessionId()!,
        UserId: targetId ?? this.session.userId()!,
        ...params
      });
      const item = clean(extractData<ApiUserCustomization>(response, action, "UserCustomization"));
      this._state.update(s => ({
        ...s,
        data: { ...s.data, [item.userId]: item },
        loading: false,
        loaded: true,
        errors: []
      }));
      return item;
    } catch (e) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }
}
